import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import Calculators from "./pages/Calculators";
import Converters from "./pages/Converters";
import SpecialCalculators from "./pages/SpecialCalculators";
import NotFound from "./pages/NotFound";
import Blog from "./pages/Blog";
import BlogPostPage from "./pages/BlogPostPage";
import TipCalculatorPage from "./pages/special/TipCalculator";
import MortgageCalculatorPage from "./pages/special/MortgageCalculator";
import LoanCalculatorPage from "./pages/special/LoanCalculator";
import BMICalculatorPage from "./pages/special/BMICalculator";
import PercentageCalculatorPage from "./pages/special/PercentageCalculator";
import SitemapPage from "./pages/SitemapPage";

// Import calculator pages
import StandardCalculatorPage from "./pages/calculators/StandardCalculator";
import ScientificCalculatorPage from "./pages/calculators/ScientificCalculator";
import GraphingCalculatorPage from "./pages/calculators/GraphingCalculator";
import ProgrammingCalculatorPage from "./pages/calculators/ProgrammingCalculator";
import DateCalculatorPage from "./pages/calculators/DateCalculator";

// Import financial calculator pages
import AutoCalculatorPage from "./pages/special/AutoCalculator";
import InvestmentCalculatorPage from "./pages/special/InvestmentCalculator";
import RetirementCalculatorPage from "./pages/special/RetirementCalculator";
import TaxCalculatorPage from "./pages/special/TaxCalculator";
import SalaryCalculatorPage from "./pages/special/SalaryCalculator";
import InterestCalculatorPage from "./pages/special/InterestCalculator";
import DebtCalculatorPage from "./pages/special/DebtCalculator";

// Import health calculator pages
import AgeCalculatorPage from "./pages/special/AgeCalculator";
import CalorieCalculatorPage from "./pages/special/CalorieCalculator";
import BodyFatCalculatorPage from "./pages/special/BodyFatCalculator";
import BMRCalculatorPage from "./pages/special/BMRCalculator";

// Import pregnancy calculator pages
import PregnancyCalculatorPage from "./pages/special/PregnancyCalculator";
import PregnancyWeightCalculatorPage from "./pages/special/PregnancyWeightCalculator";
import PregnancyConceptionCalculatorPage from "./pages/special/PregnancyConceptionCalculator";
import DueDateCalculatorPage from "./pages/special/DueDateCalculator";
import OvulationCalculatorPage from "./pages/special/OvulationCalculator";
import ConceptionCalculatorPage from "./pages/special/ConceptionCalculator";
import PeriodCalculatorPage from "./pages/special/PeriodCalculator";

// Import converter pages
import LengthConverterPage from "./pages/converters/LengthConverterPage";
import WeightConverterPage from "./pages/converters/WeightConverterPage";
import VolumeConverterPage from "./pages/converters/VolumeConverterPage";
import TemperatureConverterPage from "./pages/converters/TemperatureConverterPage";
import AreaConverterPage from "./pages/converters/AreaConverterPage";
import PressureConverterPage from "./pages/converters/PressureConverterPage";
import EnergyConverterPage from "./pages/converters/EnergyConverterPage";
import PowerConverterPage from "./pages/converters/PowerConverterPage";
import ForceConverterPage from "./pages/converters/ForceConverterPage";
import TimeConverterPage from "./pages/converters/TimeConverterPage";
import SpeedConverterPage from "./pages/converters/SpeedConverterPage";
import AngleConverterPage from "./pages/converters/AngleConverterPage";
import FuelConsumptionConverterPage from "./pages/converters/FuelConsumptionConverterPage";
import DigitalStorageConverterPage from "./pages/converters/DigitalStorageConverterPage";
import CurrencyConverterPage from "./pages/converters/CurrencyConverterPage";
import DataTransferRateConverterPage from "./pages/converters/DataTransferRateConverterPage";

// Import new calculator pages
import CompoundInterestCalculatorPage from "./pages/special/CompoundInterestCalculator";
import ROICalculatorPage from "./pages/special/ROICalculator";
import NPVCalculatorPage from "./pages/special/NPVCalculator";
import CAGRCalculatorPage from "./pages/special/CAGRCalculator";
import DTICalculatorPage from "./pages/special/DTICalculator";
import DiscountCalculatorPage from "./pages/special/DiscountCalculator";
import EmergencyFundCalculatorPage from "./pages/special/EmergencyFundCalculator";
import NetWorthCalculatorPage from "./pages/special/NetWorthCalculator";
import EBITDACalculatorPage from "./pages/special/EBITDACalculator";
import ProfitMarginCalculatorPage from "./pages/special/ProfitMarginCalculator";
import GrossMarginCalculatorPage from "./pages/special/GrossMarginCalculator";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/calculators" element={<Calculators />} />
          <Route path="/converters" element={<Converters />} />
          <Route path="/special" element={<SpecialCalculators />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          
          {/* Basic Calculator Routes */}
          <Route path="/calculators/standard" element={<StandardCalculatorPage />} />
          <Route path="/calculators/scientific" element={<ScientificCalculatorPage />} />
          <Route path="/calculators/graphing" element={<GraphingCalculatorPage />} />
          <Route path="/calculators/programming" element={<ProgrammingCalculatorPage />} />
          <Route path="/calculators/date" element={<DateCalculatorPage />} />
          
          {/* Special Calculator Routes */}
          <Route path="/special/tip" element={<TipCalculatorPage />} />
          <Route path="/special/mortgage" element={<MortgageCalculatorPage />} />
          <Route path="/special/loan" element={<LoanCalculatorPage />} />
          <Route path="/special/bmi" element={<BMICalculatorPage />} />
          <Route path="/special/percentage" element={<PercentageCalculatorPage />} />
          
          {/* Financial Calculator Routes */}
          <Route path="/special/auto" element={<AutoCalculatorPage />} />
          <Route path="/special/investment" element={<InvestmentCalculatorPage />} />
          <Route path="/special/retirement" element={<RetirementCalculatorPage />} />
          <Route path="/special/tax" element={<TaxCalculatorPage />} />
          <Route path="/special/salary" element={<SalaryCalculatorPage />} />
          <Route path="/special/interest" element={<InterestCalculatorPage />} />
          <Route path="/special/debt" element={<DebtCalculatorPage />} />
          
          {/* Health Calculator Routes */}
          <Route path="/special/age" element={<AgeCalculatorPage />} />
          <Route path="/special/calories" element={<CalorieCalculatorPage />} />
          <Route path="/special/bodyfat" element={<BodyFatCalculatorPage />} />
          <Route path="/special/bmr" element={<BMRCalculatorPage />} />
          
          {/* Pregnancy Calculator Routes */}
          <Route path="/special/pregnancy" element={<PregnancyCalculatorPage />} />
          <Route path="/special/pregnancy-weight" element={<PregnancyWeightCalculatorPage />} />
          <Route path="/special/pregnancy-conception" element={<PregnancyConceptionCalculatorPage />} />
          <Route path="/special/due-date" element={<DueDateCalculatorPage />} />
          <Route path="/special/ovulation" element={<OvulationCalculatorPage />} />
          <Route path="/special/conception" element={<ConceptionCalculatorPage />} />
          <Route path="/special/period" element={<PeriodCalculatorPage />} />
          
          {/* Converter Routes */}
          <Route path="/converters/length" element={<LengthConverterPage />} />
          <Route path="/converters/weight" element={<WeightConverterPage />} />
          <Route path="/converters/volume" element={<VolumeConverterPage />} />
          <Route path="/converters/temperature" element={<TemperatureConverterPage />} />
          <Route path="/converters/area" element={<AreaConverterPage />} />
          <Route path="/converters/pressure" element={<PressureConverterPage />} />
          <Route path="/converters/energy" element={<EnergyConverterPage />} />
          <Route path="/converters/power" element={<PowerConverterPage />} />
          <Route path="/converters/force" element={<ForceConverterPage />} />
          <Route path="/converters/time" element={<TimeConverterPage />} />
          <Route path="/converters/speed" element={<SpeedConverterPage />} />
          <Route path="/converters/angle" element={<AngleConverterPage />} />
          <Route path="/converters/fuel" element={<FuelConsumptionConverterPage />} />
          <Route path="/converters/digital" element={<DigitalStorageConverterPage />} />
          <Route path="/converters/currency" element={<CurrencyConverterPage />} />
          <Route path="/converters/data-transfer" element={<DataTransferRateConverterPage />} />
          
          {/* New Financial Calculator Routes */}
          <Route path="/special/compound-interest" element={<CompoundInterestCalculatorPage />} />
          <Route path="/special/roi" element={<ROICalculatorPage />} />
          <Route path="/special/npv" element={<NPVCalculatorPage />} />
          <Route path="/special/cagr" element={<CAGRCalculatorPage />} />
          <Route path="/special/dti" element={<DTICalculatorPage />} />
          <Route path="/special/discount" element={<DiscountCalculatorPage />} />
          <Route path="/special/emergency-fund" element={<EmergencyFundCalculatorPage />} />
          <Route path="/special/net-worth" element={<NetWorthCalculatorPage />} />
          <Route path="/special/ebitda" element={<EBITDACalculatorPage />} />
          <Route path="/special/profit-margin" element={<ProfitMarginCalculatorPage />} />
          <Route path="/special/gross-margin" element={<GrossMarginCalculatorPage />} />
          
          {/* This needs to be the last route so it catches all unmatched routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
